import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useAchievements } from "@/hooks/useAchievements";
import { Target, Lock } from "lucide-react";
import { useMemo } from "react";

interface AchievementWithProgress {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  badge_color: string;
  requirement_count: number;
  currentProgress: number;
  percentComplete: number;
}

export function NextAchievements() {
  const { achievements, userAchievements } = useAchievements();

  const nextAchievements = useMemo(() => {
    if (!achievements || !userAchievements) return [];

    // Map achievements with their progress
    const achievementsWithProgress: AchievementWithProgress[] = achievements
      .map((achievement) => {
        const userAchievement = userAchievements.find(
          (ua) => ua.achievement_id === achievement.id
        );
        
        const currentProgress = userAchievement?.progress || 0;
        const isUnlocked = userAchievement?.unlocked_at !== null && userAchievement?.unlocked_at !== undefined;
        const percentComplete = Math.min(
          (currentProgress / (achievement.requirement_count || 1)) * 100,
          100
        );

        return {
          ...achievement,
          points: achievement.points || 0,
          requirement_count: achievement.requirement_count || 1,
          currentProgress,
          percentComplete,
          isUnlocked,
        };
      })
      .filter((a) => !a.isUnlocked) // Only show not unlocked
      .sort((a, b) => b.percentComplete - a.percentComplete) // Sort by closest to completion
      .slice(0, 3); // Get top 3

    return achievementsWithProgress;
  }, [achievements, userAchievements]);

  if (!achievements || nextAchievements.length === 0) {
    return null;
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Próximas Conquistas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {nextAchievements.map((achievement) => (
          <div
            key={achievement.id}
            className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${achievement.badge_color}20` }}
            >
              {achievement.percentComplete > 0 ? (
                <Target
                  className="h-5 w-5"
                  style={{ color: achievement.badge_color }}
                />
              ) : (
                <Lock className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-medium text-sm truncate">{achievement.name}</h4>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  +{achievement.points} pts
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {achievement.description}
              </p>
              <div className="mt-2 space-y-1">
                <Progress
                  value={achievement.percentComplete}
                  className="h-2"
                  style={
                    {
                      "--progress-background": achievement.badge_color,
                    } as React.CSSProperties
                  }
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {achievement.currentProgress}/{achievement.requirement_count}
                  </span>
                  <span>{Math.round(achievement.percentComplete)}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {nextAchievements.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            🎉 Você desbloqueou todas as conquistas!
          </p>
        )}
      </CardContent>
    </Card>
  );
}
